import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { hasExternalServicesConsent, useCookieConsent } from '@/features/cookie-consent';

import { Coordinates } from '@/shared/lib/location';

import * as S from './contacts.styled';

type YandexEventMapProps = {
  coordinates: Coordinates;
  title: string;
  address: string;
};

type YandexMapInstance = {
  destroy: () => void;
  geoObjects: {
    add: (geoObject: unknown) => void;
  };
  behaviors: {
    disable: (behavior: string) => void;
  };
};

type YandexMapsApi = {
  ready: (callback: () => void) => void;
  Map: new (
    element: HTMLElement,
    state: {
      center: [number, number];
      zoom: number;
      controls: string[];
    },
    options?: Record<string, unknown>,
  ) => YandexMapInstance;
  Placemark: new (
    coordinates: [number, number],
    properties?: Record<string, unknown>,
    options?: Record<string, unknown>,
  ) => unknown;
};

declare global {
  interface Window {
    ymaps?: YandexMapsApi;
  }
}

let yandexMapsPromise: Promise<YandexMapsApi> | null = null;

function getYandexMapsLang(language: string): string {
  if (language.startsWith('en')) {
    return 'en_US';
  }

  return 'ru_RU';
}

function loadYandexMaps(lang: string): Promise<YandexMapsApi> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Yandex Maps is available only in browser'));
  }

  if (window.ymaps) {
    return new Promise((resolve) => {
      window.ymaps?.ready(() => resolve(window.ymaps as YandexMapsApi));
    });
  }

  if (yandexMapsPromise) {
    return yandexMapsPromise;
  }

  const apiKey = process.env.NEXT_PUBLIC_YANDEX_MAPS_API_KEY;

  if (!apiKey) {
    return Promise.reject(new Error('NEXT_PUBLIC_YANDEX_MAPS_API_KEY is not defined'));
  }

  yandexMapsPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://api-maps.yandex.ru/2.1/?apikey=${encodeURIComponent(apiKey)}&lang=${lang}`;
    script.async = true;
    script.dataset.yandexMaps = 'true';
    script.onload = () => {
      if (!window.ymaps) {
        yandexMapsPromise = null;
        script.remove();
        reject(new Error('Yandex Maps API did not initialize'));
        return;
      }

      window.ymaps.ready(() => resolve(window.ymaps as YandexMapsApi));
    };
    script.onerror = () => {
      yandexMapsPromise = null;
      script.remove();
      reject(new Error('Failed to load Yandex Maps API'));
    };

    document.head.appendChild(script);
  });

  return yandexMapsPromise;
}

export const YandexEventMap: React.FC<YandexEventMapProps> = ({ coordinates, title, address }) => {
  const { t, i18n } = useTranslation('event');
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<YandexMapInstance | null>(null);
  const [consent, chooseConsent] = useCookieConsent();
  const externalServicesAllowed = hasExternalServicesConsent(consent ?? null);
  const [status, setStatus] = useState<'blocked' | 'loading' | 'ready' | 'error'>('blocked');
  const [retryAttempt, setRetryAttempt] = useState(0);

  useEffect(() => {
    let isMounted = true;
    const center: [number, number] = [coordinates.latitude, coordinates.longitude];

    mapInstanceRef.current?.destroy();
    mapInstanceRef.current = null;

    if (!externalServicesAllowed) {
      setStatus('blocked');
      return () => {
        isMounted = false;
      };
    }

    setStatus('loading');

    loadYandexMaps(getYandexMapsLang(i18n.language))
      .then((ymaps) => {
        if (!isMounted || !mapRef.current) {
          return;
        }

        const map = new ymaps.Map(
          mapRef.current,
          {
            center,
            zoom: 15,
            controls: ['zoomControl', 'fullscreenControl'],
          },
          {
            suppressMapOpenBlock: true,
          },
        );

        const placemark = new ymaps.Placemark(
          center,
          {
            balloonContentHeader: title,
            balloonContentBody: address,
            hintContent: address || title,
          },
          {
            iconColor: '#833FDC',
            preset: 'islands#violetDotIcon',
          },
        );

        map.geoObjects.add(placemark);
        map.behaviors.disable('scrollZoom');
        mapInstanceRef.current = map;
        setStatus('ready');
      })
      .catch(() => {
        if (isMounted) {
          setStatus('error');
        }
      });

    return () => {
      isMounted = false;
      mapInstanceRef.current?.destroy();
      mapInstanceRef.current = null;
    };
  }, [
    address,
    coordinates.latitude,
    coordinates.longitude,
    externalServicesAllowed,
    i18n.language,
    retryAttempt,
    title,
  ]);

  return (
    <S.MapFrame>
      <S.MapCanvas ref={mapRef} />
      {status === 'blocked' && (
        <S.MapState>
          <span>{t('CONTACTS_MAP_CONSENT_REQUIRED')}</span>
          <S.MapConsentButton type="button" onClick={() => chooseConsent('external')}>
            {t('CONTACTS_MAP_CONSENT_ACTION')}
          </S.MapConsentButton>
        </S.MapState>
      )}
      {status === 'loading' && <S.MapState>{t('CONTACTS_MAP_LOADING')}</S.MapState>}
      {status === 'error' && (
        <S.MapState>
          <span>{t('CONTACTS_MAP_UNAVAILABLE')}</span>
          <S.MapConsentButton type="button" onClick={() => setRetryAttempt((attempt) => attempt + 1)}>
            {t('CONTACTS_MAP_RETRY')}
          </S.MapConsentButton>
        </S.MapState>
      )}
    </S.MapFrame>
  );
};
